<section class="sa-getting-started" aria-labelledby="sa-getting-started-title">
  <header class="sa-getting-started__header">
    <h2 id="sa-getting-started-title" class="sa-getting-started__title">
      {{ config.copy.gettingStartedTitle }}
    </h2>
    <p class="sa-getting-started__subtitle">{{ config.copy.gettingStartedSubtitle }}</p>
  </header>

  <nav class="sa-getting-started__stepper" aria-label="Setup progress">
    <ol class="sa-getting-started__stepper-list">
      <li
        *ngFor="let step of steps; let i = index"
        class="sa-getting-started__stepper-item"
        [class.sa-getting-started__stepper-item--active]="step.key === activeStep"
        [class.sa-getting-started__stepper-item--done]="i < activeStepIndex"
        [class.sa-getting-started__stepper-item--locked]="step.key === 'complete' && !configSaved"
      >
        <button
          type="button"
          class="sa-getting-started__stepper-btn"
          [class.sa-getting-started__stepper-btn--active]="step.key === activeStep"
          [attr.aria-current]="step.key === activeStep ? 'step' : null"
          [attr.title]="step.description || step.label"
          [disabled]="step.key === 'complete' && !configSaved"
          (click)="goToStep(step.key)"
        >
          <span class="sa-getting-started__stepper-index">{{ i + 1 }}</span>
          <span class="sa-getting-started__stepper-label">{{ step.label }}</span>
        </button>
      </li>
    </ol>
  </nav>

  <div class="sa-getting-started__panel">
    <ng-container *ngIf="activeStep === 'industry'">
      <h3 class="sa-getting-started__panel-title">Industry details</h3>
      <p class="sa-getting-started__step-description">{{ industryCopy.description }}</p>
      <p class="sa-getting-started__config-note">{{ industryCopy.configNote }}</p>
      <div class="sa-getting-started__input-block">
        <label class="sa-getting-started__input-label" for="sa-industry-name">{{ industryCopy.nameLabel }}</label>
        <p class="sa-getting-started__field-hint">{{ industryCopy.nameHint }}</p>
        <input
          id="sa-industry-name"
          type="text"
          class="sa-getting-started__text-input"
          [(ngModel)]="draft.industryName"
          placeholder="e.g. Whatsapp Banking"
        />
      </div>
      <div class="sa-getting-started__input-block">
        <label class="sa-getting-started__input-label" for="sa-industry-desc">{{ industryCopy.descriptionLabel }}</label>
        <p class="sa-getting-started__field-hint">{{ industryCopy.descriptionHint }}</p>
        <textarea
          id="sa-industry-desc"
          class="sa-getting-started__text-input sa-getting-started__textarea"
          rows="3"
          [(ngModel)]="draft.industryDescription"
        ></textarea>
      </div>
    </ng-container>

    <ng-container *ngIf="activeStep === 'taxonomy'">
      <h3 class="sa-getting-started__panel-title">Taxonomy</h3>
      <p class="sa-getting-started__step-description">{{ taxonomyCopy.description }}</p>
      <p class="sa-getting-started__config-note">{{ taxonomyCopy.configNote }}</p>
      <div class="sa-getting-started__info-card">
        <h4 class="sa-getting-started__info-card-title">CSV format</h4>
        <p class="sa-getting-started__info-card-body">
          Columns: <code>{{ taxonomyCopy.csvColumns }}</code>
        </p>
        <p class="sa-getting-started__info-card-hint">{{ taxonomyCopy.csvHint }}</p>
      </div>
      <ng-container *ngIf="config.useInsightsEngine; else uploadDisabled">
        <div class="sa-getting-started__input-block">
          <label class="sa-getting-started__input-label">Upload taxonomy CSV</label>
          <div class="sa-getting-started__file-row">
            <label class="sa-getting-started__file-btn">
              <input
                type="file"
                accept=".csv,text/csv"
                class="sa-getting-started__file-input"
                [disabled]="!canUpload || saving"
                (change)="onTaxonomyCsvSelected($event)"
              />
              {{ buttons.chooseCsv }}
            </label>
            <span *ngIf="taxonomyCsvFileName" class="sa-getting-started__filename">{{ taxonomyCsvFileName }}</span>
          </div>
          <p *ngIf="!canUpload" class="sa-getting-started__warn">{{ taxonomyCopy.noPermission }}</p>
          <p *ngIf="taxonomyPreviewLoading" class="sa-getting-started__status">Parsing CSV…</p>
          <p *ngIf="taxonomyPreviewError" class="sa-getting-started__error">{{ taxonomyPreviewError }}</p>
          <p
            *ngIf="draft.taxonomy?.themes?.length && !taxonomyPreviewError"
            class="sa-getting-started__preview-summary"
          >
            {{ draft.taxonomy?.themes?.length }} theme(s) loaded. Saved on the final step.
          </p>
          <app-sentiment-analysis-taxonomy-preview
            *ngIf="draft.taxonomy?.themes?.length && !taxonomyPreviewError"
            [taxonomy]="draft.taxonomy"
            [title]="taxonomyCsvFileName ? 'Taxonomy preview' : 'Taxonomy details'"
          />
        </div>
      </ng-container>
      <ng-template #uploadDisabled>
        <p class="sa-getting-started__error">Insights engine is not enabled in this environment.</p>
      </ng-template>
    </ng-container>

    <ng-container *ngIf="activeStep === 'heatmap_cohorts'">
      <h3 class="sa-getting-started__panel-title">Heatmap cohort setup</h3>
      <p class="sa-getting-started__step-description">{{ heatmapCohortsCopy.description }}</p>
      <p class="sa-getting-started__config-note">{{ heatmapCohortsCopy.configNote }}</p>
      <p *ngIf="cohortFieldsLoading" class="sa-getting-started__status">{{ heatmapCohortsCopy.loadingHint }}</p>
      <p *ngIf="!cohortFieldsLoading && !cohortFieldOptions.length" class="sa-getting-started__hint">
        {{ heatmapCohortsCopy.emptyHint }}
      </p>
      <p *ngIf="cohortFieldOptions.length" class="sa-getting-started__preview-summary">
        {{ cohortFieldOptions.length }} tag field(s) —
        {{ heatmapCohortsCopy.selectedCountLabel }}: {{ cohortSelectedCount('heatmap') }}
      </p>
      <ul *ngIf="cohortFieldOptions.length" class="sa-getting-started__checkbox-list">
        <li
          *ngFor="let opt of cohortFieldOptions; trackBy: trackCohortField"
          class="sa-getting-started__checkbox-item"
        >
          <label
            class="sa-getting-started__cohort-label"
            (click)="onCohortCheckboxClick(opt.fieldId, opt.label, 'heatmap', $event)"
          >
            <input
              type="checkbox"
              class="sa-getting-started__cohort-checkbox"
              tabindex="-1"
              [checked]="isCohortFieldSelected(opt.fieldId, 'heatmap')"
              (click)="$event.preventDefault()"
            />
            <span>{{ opt.label }}</span>
            <code class="sa-getting-started__field-code">{{ opt.fieldId }}</code>
          </label>
        </li>
      </ul>
    </ng-container>

    <ng-container *ngIf="activeStep === 'analysis_input'">
      <h3 class="sa-getting-started__panel-title">Rating alignment</h3>
      <p class="sa-getting-started__step-description">{{ analysisInputCopy.description }}</p>
      <p class="sa-getting-started__config-note">{{ analysisInputCopy.configNote }}</p>

      <div *ngIf="isRatingAlignmentConfigured" class="sa-getting-started__info-card sa-getting-started__info-card--success">
        <ng-container *ngIf="ratingAlignmentResolved === 'nps'">
          <h4 class="sa-getting-started__info-card-title">{{ analysisInputCopy.configuredNpsTitle }}</h4>
          <p class="sa-getting-started__info-card-body">{{ analysisInputCopy.configuredNpsBody }}</p>
        </ng-container>
        <ng-container *ngIf="ratingAlignmentResolved === 'csat'">
          <h4 class="sa-getting-started__info-card-title">{{ analysisInputCopy.configuredCsatTitle }}</h4>
          <p class="sa-getting-started__info-card-body">{{ analysisInputCopy.configuredCsatBody }}</p>
        </ng-container>
      </div>

      <div *ngIf="!isRatingAlignmentConfigured" class="sa-getting-started__warn-card" role="alert">
        <h4 class="sa-getting-started__warn-card-title">{{ analysisInputCopy.notConfiguredTitle }}</h4>
        <p class="sa-getting-started__warn-card-lead">{{ analysisInputCopy.notConfiguredLead }}</p>
        <p class="sa-getting-started__section-label">What to do</p>
        <ol class="sa-getting-started__warn-steps">
          <li *ngFor="let step of analysisInputCopy.notConfiguredSteps">{{ step }}</li>
        </ol>
        <p class="sa-getting-started__warn-card-footer">{{ analysisInputCopy.notConfiguredFooter }}</p>
      </div>
    </ng-container>

    <ng-container *ngIf="activeStep === 'prompt'">
      <h3 class="sa-getting-started__panel-title">Prompt setup</h3>
      <p class="sa-getting-started__step-description">{{ promptCopy.description }}</p>
      <p class="sa-getting-started__config-note">{{ promptCopy.configNote }}</p>
      <div class="sa-getting-started__input-block">
        <label class="sa-getting-started__input-label" for="sa-theme-mode">{{ promptCopy.themeModeLabel }}</label>
        <select id="sa-theme-mode" class="sa-getting-started__text-input" [(ngModel)]="draft.taxonomyThemeMode">
          <option *ngFor="let m of taxonomyThemeModeOptions" [value]="m.value">{{ m.label }}</option>
        </select>
      </div>
      <div class="sa-getting-started__input-block">
        <label class="sa-getting-started__input-label" for="sa-subtheme-mode">{{ promptCopy.subthemeModeLabel }}</label>
        <select id="sa-subtheme-mode" class="sa-getting-started__text-input" [(ngModel)]="draft.taxonomySubthemeMode">
          <option *ngFor="let m of taxonomyThemeModeOptions" [value]="m.value">{{ m.label }}</option>
        </select>
      </div>
      <div class="sa-getting-started__input-block">
        <label class="sa-getting-started__input-label" for="sa-theme-instruction">{{ promptCopy.themeInstructionLabel }}</label>
        <p class="sa-getting-started__field-hint">{{ promptCopy.themeInstructionHint }}</p>
        <textarea
          id="sa-theme-instruction"
          class="sa-getting-started__text-input sa-getting-started__textarea"
          rows="5"
          [(ngModel)]="draft.themeDomainInstruction"
          placeholder="Domain rules for theme classification (optional)"
        ></textarea>
      </div>
    </ng-container>

    <ng-container *ngIf="activeStep === 'summary'">
      <h3 class="sa-getting-started__panel-title">Summary</h3>
      <p class="sa-getting-started__step-description">{{ summaryCopy.description }}</p>
      <p class="sa-getting-started__config-note">{{ summaryCopy.configNote }}</p>

      <h4 class="sa-getting-started__section-label">{{ summaryCohortsCopy.selectedCountLabel }}</h4>
      <p class="sa-getting-started__hint">{{ summaryCohortsCopy.description }}</p>
      <p class="sa-getting-started__field-hint">{{ summaryCohortsCopy.copyFromHeatmapHint }}</p>
      <p *ngIf="cohortFieldsLoading" class="sa-getting-started__status">{{ summaryCohortsCopy.loadingHint }}</p>
      <p *ngIf="!cohortFieldsLoading && !cohortFieldOptions.length" class="sa-getting-started__hint">
        {{ summaryCohortsCopy.emptyHint }}
      </p>
      <div *ngIf="cohortFieldOptions.length" class="sa-getting-started__actions">
        <button type="button" class="sa-getting-started__btn" (click)="copySummaryCohortsFromHeatmap()">
          Copy heatmap selection
        </button>
      </div>
      <p *ngIf="cohortFieldOptions.length" class="sa-getting-started__preview-summary">
        {{ summaryCohortsCopy.selectedCountLabel }}: {{ cohortSelectedCount('summary') }}
      </p>
      <ul *ngIf="cohortFieldOptions.length" class="sa-getting-started__checkbox-list">
        <li
          *ngFor="let opt of cohortFieldOptions; trackBy: trackCohortField"
          class="sa-getting-started__checkbox-item"
        >
          <label
            class="sa-getting-started__cohort-label"
            (click)="onCohortCheckboxClick(opt.fieldId, opt.label, 'summary', $event)"
          >
            <input
              type="checkbox"
              class="sa-getting-started__cohort-checkbox"
              tabindex="-1"
              [checked]="isCohortFieldSelected(opt.fieldId, 'summary')"
              (click)="$event.preventDefault()"
            />
            <span>{{ opt.label }}</span>
            <code class="sa-getting-started__field-code">{{ opt.fieldId }}</code>
          </label>
        </li>
      </ul>

      <h4 class="sa-getting-started__section-label sa-getting-started__section-label--spaced">Precompute</h4>
      <label class="sa-getting-started__checkbox-item sa-getting-started__checkbox-item--inline sa-getting-started__checkbox-item--stacked">
        <span class="sa-getting-started__widget-label">
          <input type="checkbox" [(ngModel)]="draft.summaryPrecomputeEnabled" />
          <span class="sa-getting-started__widget-label-text">{{ summaryCopy.precomputeLabel }}</span>
        </span>
        <p class="sa-getting-started__widget-desc">{{ summaryCopy.precomputeHint }}</p>
      </label>
      <ng-container *ngIf="draft.summaryPrecomputeEnabled">
        <h4 class="sa-getting-started__section-label">{{ summaryCopy.sentimentsTitle }}</h4>
        <p class="sa-getting-started__hint">{{ summaryCopy.sentimentsHint }}</p>
        <p
          *ngIf="!draft.summaryPrecomputeSentiments.length"
          class="sa-getting-started__warn"
        >
          {{ summaryCopy.sentimentsEmpty }}
        </p>
        <ul class="sa-getting-started__checkbox-list">
          <li
            *ngFor="let id of summarySentimentChoices"
            class="sa-getting-started__checkbox-item"
          >
            <label
              class="sa-getting-started__cohort-label"
              (click)="onSummarySentimentCheckboxClick(id, $event)"
            >
              <input
                type="checkbox"
                class="sa-getting-started__cohort-checkbox"
                tabindex="-1"
                [checked]="isSummarySentimentSelected(id)"
                (click)="$event.preventDefault()"
              />
              <span>{{ summarySentimentChoiceLabel(id) }}</span>
              <code class="sa-getting-started__field-code">{{ id }}</code>
            </label>
          </li>
        </ul>

        <h4 class="sa-getting-started__section-label sa-getting-started__section-label--spaced">{{ summaryCopy.themeAllowlistTitle }}</h4>
        <p class="sa-getting-started__hint">{{ summaryCopy.themeAllowlistHint }}</p>
        <p
          *ngIf="!draft.summaryThemeAllowlist.length && taxonomyThemeNames.length"
          class="sa-getting-started__warn"
        >
          {{ summaryCopy.themeAllowlistEmpty }}
        </p>
        <ul *ngIf="draft.summaryThemeAllowlist.length" class="sa-getting-started__theme-allowlist">
          <li *ngFor="let name of draft.summaryThemeAllowlist" class="sa-getting-started__theme-allowlist-item">
            <span class="sa-getting-started__theme-allowlist-name">{{ name }}</span>
            <button
              type="button"
              class="sa-getting-started__btn sa-getting-started__btn--small"
              (click)="removeThemeFromAllowlist(name)"
            >
              {{ summaryCopy.removeThemeButton }}
            </button>
          </li>
        </ul>
        <div *ngIf="taxonomyThemeNames.length" class="sa-getting-started__input-block sa-getting-started__theme-add-row">
          <label class="sa-getting-started__input-label" for="sa-summary-theme-add">{{
            summaryCopy.addThemeLabel
          }}</label>
          <div class="sa-getting-started__file-row">
            <select
              id="sa-summary-theme-add"
              class="sa-getting-started__text-input sa-getting-started__theme-select"
              [(ngModel)]="summaryThemeToAdd"
              [ngModelOptions]="{ standalone: true }"
            >
              <option value="">{{ summaryCopy.addThemePlaceholder }}</option>
              <option *ngFor="let name of themesAvailableToAdd" [value]="name">{{ name }}</option>
            </select>
            <button
              type="button"
              class="sa-getting-started__btn sa-getting-started__btn--primary"
              [disabled]="!summaryThemeToAdd"
              (click)="addSelectedThemeToAllowlist()"
            >
              {{ summaryCopy.addThemeButton }}
            </button>
            <button
              type="button"
              class="sa-getting-started__btn"
              [disabled]="!themesAvailableToAdd.length"
              (click)="addAllTaxonomyThemesToAllowlist()"
            >
              {{ summaryCopy.addAllThemesButton }}
            </button>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="activeStep === 'complete'">
      <h3 class="sa-getting-started__panel-title">Setup complete</h3>
      <p class="sa-getting-started__step-description">{{ completeCopy.description }}</p>
      <div class="sa-getting-started__success-icon" aria-hidden="true">✓</div>
    </ng-container>
  </div>

  <footer
    class="sa-getting-started__footer"
    [class.sa-getting-started__footer--primary-only]="activeStep === 'complete'"
  >
    <button
      *ngIf="activeStep !== 'complete'"
      type="button"
      class="sa-getting-started__btn"
      [disabled]="isFirstStep || saving"
      (click)="goBack()"
    >
      {{ buttons.back }}
    </button>
    <button
      *ngIf="activeStep === 'summary'"
      type="button"
      class="sa-getting-started__btn sa-getting-started__btn--primary"
      [disabled]="!canSaveFinalConfig || saving"
      (click)="saveFullConfiguration()"
    >
      {{ saving ? buttons.saving : buttons.saveAndContinue }}
    </button>
    <button
      *ngIf="activeStep === 'complete'"
      type="button"
      class="sa-getting-started__btn sa-getting-started__btn--primary"
      (click)="finishAndLoadDashboard()"
    >
      {{ buttons.goToDashboard }}
    </button>
    <button
      *ngIf="activeStep !== 'summary' && activeStep !== 'complete'"
      type="button"
      class="sa-getting-started__btn sa-getting-started__btn--primary"
      [disabled]="!canGoNext()"
      (click)="goNext()"
    >
      {{ buttons.next }}
    </button>
  </footer>
</section>
